import {CustomBiomarkerConfig} from '../types/biomarker';
import {BIOMARKER_STATUS_COLORS} from '../constants/colors';

/**
 * Custom biomarker configurations for specific tests that require
 * non-standard ranges, zone distributions, or display behaviors.
 */

// 3-zone biomarkers with custom 10%-80%-10% distribution
const CORTISOL_CONFIG: CustomBiomarkerConfig = {
    name: 'Cortisol',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 5,
            max: 17,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 17,
            max: 22.7,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const DHEAS_CONFIG: CustomBiomarkerConfig = {
    name: "DHEA's",
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 8,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 8,
            max: 188,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 188,
            max: 250,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

// Gender-specific hormone biomarkers
const TESTOSTERONE_MEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Testosterone',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 300,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 300,
            max: 1000,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 1000,
            max: 1250,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const TESTOSTERONE_WOMEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Testosterone',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 15,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 15,
            max: 70,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 70,
            max: 88,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const BIOAVAILABLE_TESTOSTERONE_MEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Bioavailable Testosterone',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 130,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 130,
            max: 680,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 680,
            max: 850,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const BIOAVAILABLE_TESTOSTERONE_WOMEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Bioavailable Testosterone',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 1.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 1.0,
            max: 9.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 9.0,
            max: 11.3,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const FREE_TESTOSTERONE_MEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Free Testosterone',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 5,
            max: 21,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 21,
            max: 26,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const FREE_TESTOSTERONE_WOMEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Free Testosterone',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 0.02,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 0.02,
            max: 0.5,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 0.5,
            max: 0.63,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const SHBG_MEN_CONFIG: CustomBiomarkerConfig = {
    name: 'SHBG',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 10,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 10,
            max: 57,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 57,
            max: 71,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const SHBG_WOMEN_CONFIG: CustomBiomarkerConfig = {
    name: 'SHBG',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 18,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 18,
            max: 144,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 144,
            max: 180,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const PROLACTIN_MEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Prolactin',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 4.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 4.0,
            max: 15.2,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 15.2,
            max: 19.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const PROLACTIN_WOMEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Prolactin',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 4.8,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 4.8,
            max: 23.3,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 23.3,
            max: 29.1,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

// Estradiol - value only display (no graphic, value only with dropdown)
const ESTRADIOL_CONFIG: CustomBiomarkerConfig = {
    name: 'Estradiol',
    zoneCount: 3,
    zones: [],
    valueOnlyDisplay: true,
    displayContext: 'both', // Hide chart/progress bar in both dashboard and single result views
    referenceText: `Women (premenopausal):
• Early follicular phase: 20.0 – 157 pg/mL
• Mid-follicular phase: 20.7 – 139 pg/mL
• Ovulation peak: 19.9 – 622 pg/mL
• Mid-luteal phase: 25.8 – 279 pg/mL

Women (postmenopausal):
• < 15 – 30 pg/mL

Men (adult):
• 11 – 44 pg/mL`,
};

// FSH - value only display (no graphic, value only with dropdown)
const FSH_CONFIG: CustomBiomarkerConfig = {
    name: 'FSH',
    zoneCount: 3,
    zones: [],
    valueOnlyDisplay: true,
    displayContext: 'both',
    referenceText: `Women (premenopausal):
• Follicular phase: 3.0 – 8.0 mIU/mL
• Ovulatory peak: 2.6 – 16.9 mIU/mL
• Luteal phase: 1.4 – 5.5 mIU/mL

Women (postmenopausal):
• 26 – 135 mIU/mL (FSH rises significantly after menopause)

Men (adult):
• 1.5 – 12.4 mIU/mL`,
};

// LH - value only display (no graphic, value only with dropdown)
const LH_CONFIG: CustomBiomarkerConfig = {
    name: 'LH',
    zoneCount: 3,
    zones: [],
    valueOnlyDisplay: true,
    displayContext: 'both',
    referenceText: `Women (premenopausal):
• Follicular phase: 2.4 – 12.6 mIU/mL
• Ovulation peak: 14.0 – 95.6 mIU/mL
• Luteal phase: 1.0 – 11.4 mIU/mL

Women (postmenopausal):
• 7.7 – 58.5 mIU/mL

Men (adult):
• 1.7 – 8.6 mIU/mL`,
};

// Progesterone - value only display (no graphic, value only with dropdown)
const PROGESTERONE_CONFIG: CustomBiomarkerConfig = {
    name: 'Progesterone',
    zoneCount: 3,
    zones: [],
    valueOnlyDisplay: true,
    displayContext: 'both',
    referenceText: `Women (premenopausal):
• Follicular phase: 0.1 – 0.9 ng/mL
• Luteal phase: 1.8 – 23.9 ng/mL
• Ovulation peak: 0.1 – 12.0 ng/mL

Women (postmenopausal):
• 0.0 – 0.1 ng/mL

Men:
• 0.0 – 0.2 ng/mL`,
};

// Thyroid biomarkers - standard 3-zone (Red-Green-Red 10/80/10)
const FREE_T3_CONFIG: CustomBiomarkerConfig = {
    name: 'Free T3',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 2.5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 2.5,
            max: 3.9,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 3.9,
            max: 4.88,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const FREE_T4_CONFIG: CustomBiomarkerConfig = {
    name: 'Free T4',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 0.61,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 0.61,
            max: 1.12,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 1.12,
            max: 1.4,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const T3_TOTAL_CONFIG: CustomBiomarkerConfig = {
    name: 'T3 Total',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 0.97,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 0.97,
            max: 1.69,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 1.69,
            max: 2.11,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const T4_TOTAL_CONFIG: CustomBiomarkerConfig = {
    name: 'T4 Total',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 5.53,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 5.53,
            max: 11.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 11.0,
            max: 13.75,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const TSH_CONFIG: CustomBiomarkerConfig = {
    name: 'TSH',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 0.47,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 0.47,
            max: 4.68,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 4.68,
            max: 5.85,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

// Thyroid antibody biomarkers - 2-zone (Green-Red 80/20)
const THYROGLOBULIN_ANTIBODY_CONFIG: CustomBiomarkerConfig = {
    name: 'Thyroglobulin Antibody',
    zoneCount: 2,
    zones: [
        {
            label: 'Normal',
            min: 0,
            max: 40,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Normal',
        },
        {
            label: 'Elevated',
            min: 40,
            max: 100,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red
            status: 'Elevated',
        },
    ],
};

const THYROID_PEROXIDASE_ANTIBODY_CONFIG: CustomBiomarkerConfig = {
    name: 'Thyroid Peroxidase Antibody',
    zoneCount: 2,
    zones: [
        {
            label: 'Normal',
            min: 0,
            max: 9,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Normal',
        },
        {
            label: 'Elevated',
            min: 9,
            max: 25,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red
            status: 'Elevated',
        },
    ],
};

// Total Cholesterol - 4-zone Low-Optimal-Borderline-High
const TOTAL_CHOLESTEROL_CONFIG: CustomBiomarkerConfig = {
    name: 'Total Cholesterol',
    zoneCount: 4,
    forceCustomRange: true, // Force custom 4-zone layout (Low-Optimal-Borderline-High)
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 160,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 160,
            max: 200,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 200,
            max: 240,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.BORDERLINE, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 240,
            max: 350,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red
            status: 'High',
        },
    ],
};

const HDL_DIRECT_MEN_CONFIG: CustomBiomarkerConfig = {
    name: 'HDL Direct',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 40,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 40,
            max: 80,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Elevated',
            min: 90,
            max: 150,
            percentage: 33.34,
            color: BIOMARKER_STATUS_COLORS.LOW,
            status: 'Elevated',
        },
    ],
};

const HDL_DIRECT_WOMEN_CONFIG: CustomBiomarkerConfig = {
    name: 'HDL Direct',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 50,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 50,
            max: 80,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Elevated',
            min: 90,
            max: 150,
            percentage: 33.34,
            color: BIOMARKER_STATUS_COLORS.LOW,
            status: 'Elevated',
        },
    ],
};

const LDL_DIRECT_CONFIG: CustomBiomarkerConfig = {
    name: 'LDL Direct',
    zoneCount: 4,
    forceCustomRange: true, // Force custom 4-zone layout (Low-Optimal-Borderline-High)
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 60,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 60,
            max: 99,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 100,
            max: 130,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 130,
            max: 200,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const VLDL_C_CONFIG: CustomBiomarkerConfig = {
    name: 'VLDL-C',
    zoneCount: 4,
    forceCustomRange: true, // Force custom 4-zone layout (Low-Optimal-Borderline-High)
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 5,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 5,
            max: 20,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 21,
            max: 30,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 30,
            max: 50,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const TRIGLYCERIDES_CONFIG: CustomBiomarkerConfig = {
    name: 'Triglycerides',
    zoneCount: 4,
    forceCustomRange: true, // Force custom 4-zone layout (Low-Optimal-Borderline-High)
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 40,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 40,
            max: 99,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 100,
            max: 149,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 150,
            max: 250,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const HSCRP_CONFIG: CustomBiomarkerConfig = {
    name: 'hsCRP',
    zoneCount: 3,
    forceCustomRange: true, // Force custom 3-zone layout with narrow borderline range
    zones: [
        {
            label: 'Optimal',
            min: 0,
            max: 2.52,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 2.52,
            max: 3.0,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 3.0,
            max: 10,
            percentage: 33.34,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const CK_MB_CONFIG: CustomBiomarkerConfig = {
    name: 'CK-MB',
    zoneCount: 3,
    forceCustomRange: true, // Force custom 3-zone layout (Optimal-Borderline-High)
    zones: [
        {
            label: 'Optimal',
            min: 0,
            max: 5,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 5,
            max: 8,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 8,
            max: 15,
            percentage: 33.34,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const LIPOPROTEIN_A_CONFIG: CustomBiomarkerConfig = {
    name: 'Lipoprotein (a)',
    zoneCount: 3,
    forceCustomRange: true, // Force custom 3-zone layout (Optimal-Borderline-High)
    zones: [
        {
            label: 'Optimal',
            min: 0,
            max: 14,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 14,
            max: 49,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 50,
            max: 100,
            percentage: 33.34,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const APOLIPOPROTEIN_B_CONFIG: CustomBiomarkerConfig = {
    name: 'Apolipoprotein B',
    zoneCount: 4,
    forceCustomRange: true, // Force custom 4-zone layout (Low-Optimal-Borderline-High)
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 60,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 60,
            max: 89,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 90,
            max: 109,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 110,
            max: 200,
            percentage: 25,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const CHOLESTEROL_HDL_RATIO_CONFIG: CustomBiomarkerConfig = {
    name: 'Cholesterol to HDL Ratio',
    zoneCount: 3,
    forceCustomRange: true, // Force custom 3-zone layout (Optimal-Borderline-High)
    zones: [
        {
            label: 'Optimal',
            min: 0,
            max: 3.0,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 3.0,
            max: 4.4,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 4.5,
            max: 10,
            percentage: 33.34,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

// ============================================================================
// Phase 2.1: Standard Red-Green-Red (10/80/10) Biomarkers
// ============================================================================

const ALBUMIN_CONFIG: CustomBiomarkerConfig = {
    name: 'Albumin',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 3.5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 3.5,
            max: 5.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 5.0,
            max: 6.3,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const ALT_CONFIG: CustomBiomarkerConfig = {
    name: 'ALT',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 7,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 7,
            max: 56,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 56,
            max: 70,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const AMYLASE_CONFIG: CustomBiomarkerConfig = {
    name: 'Amylase',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 30,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 30,
            max: 110,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 110,
            max: 138,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const AST_CONFIG: CustomBiomarkerConfig = {
    name: 'AST',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 10,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 10,
            max: 40,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 40,
            max: 50,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const BILIRUBIN_CONFIG: CustomBiomarkerConfig = {
    name: 'Bilirubin',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 0.1,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 0.1,
            max: 1.2,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 1.2,
            max: 1.5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const BUN_CONFIG: CustomBiomarkerConfig = {
    name: 'BUN',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 7,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 7,
            max: 20,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 20,
            max: 25,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const CHLORIDE_CONFIG: CustomBiomarkerConfig = {
    name: 'Chloride',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 98,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 98,
            max: 107,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 107,
            max: 134,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const CK_CONFIG: CustomBiomarkerConfig = {
    name: 'CK',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 20,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 20,
            max: 200,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 200,
            max: 250,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const CREATININE_CONFIG: CustomBiomarkerConfig = {
    name: 'Creatinine',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 0.6,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 0.6,
            max: 1.3,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 1.3,
            max: 1.63,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const DIRECT_BILIRUBIN_CONFIG: CustomBiomarkerConfig = {
    name: 'Direct Bilirubin',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 0,
            max: 0.3,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 0.3,
            max: 0.38,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const DTIBC_CONFIG: CustomBiomarkerConfig = {
    name: 'dTIBC',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 250,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 250,
            max: 450,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 450,
            max: 563,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const ENZYMATIC_CO2_CONFIG: CustomBiomarkerConfig = {
    name: 'Enzymatic CO2',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 22,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 22,
            max: 29,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 29,
            max: 36,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const GGT_CONFIG: CustomBiomarkerConfig = {
    name: 'GGT',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 9,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 9,
            max: 48,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 48,
            max: 60,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const INSULIN_CONFIG: CustomBiomarkerConfig = {
    name: 'Insulin',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 2.6,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 2.6,
            max: 24.9,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 24.9,
            max: 31.1,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const IRON_CONFIG: CustomBiomarkerConfig = {
    name: 'Iron',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 60,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 60,
            max: 170,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 170,
            max: 213,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'High',
        },
    ],
};

const MAGNESIUM_CONFIG: CustomBiomarkerConfig = {
    name: 'Magnesium',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 1.7,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 1.7,
            max: 2.4,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 2.4,
            max: 3.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const PHOSPHORUS_CONFIG: CustomBiomarkerConfig = {
    name: 'Phosphorus',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 2.5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 2.5,
            max: 4.5,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 4.5,
            max: 5.63,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const POTASSIUM_CONFIG: CustomBiomarkerConfig = {
    name: 'Potassium',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 3.5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 3.5,
            max: 5.1,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 5.1,
            max: 6.38,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const SODIUM_CONFIG: CustomBiomarkerConfig = {
    name: 'Sodium',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 135,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 135,
            max: 145,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 145,
            max: 181,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const TIBC_CONFIG: CustomBiomarkerConfig = {
    name: 'TIBC',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 240,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 240,
            max: 450,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 450,
            max: 563,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const TOTAL_BILIRUBIN_CONFIG: CustomBiomarkerConfig = {
    name: 'Total Bilirubin',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 0.1,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 0.1,
            max: 1.2,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 1.2,
            max: 1.5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const TOTAL_PROTEIN_CONFIG: CustomBiomarkerConfig = {
    name: 'Total Protein',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 6.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 6.0,
            max: 8.3,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 8.3,
            max: 10.4,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const UIBC_CONFIG: CustomBiomarkerConfig = {
    name: 'UIBC',
    zoneCount: 3,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 155,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 155,
            max: 355,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 355,
            max: 444,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

// ============================================================================
// Phase 2.2: Red-Green-Red (10/80/10) with Custom Labels
// ============================================================================

const BUN_CREAT_RATIO_CONFIG: CustomBiomarkerConfig = {
    name: 'BUN/Creat Ratio',
    zoneCount: 3,
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 9,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.LOW,
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 9,
            max: 20,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'High',
            min: 20,
            max: 25,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const CALCIUM_CONFIG: CustomBiomarkerConfig = {
    name: 'Calcium',
    zoneCount: 3,
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 8.6,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.LOW,
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 8.6,
            max: 10.2,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'High',
            min: 10.2,
            max: 12.8,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const CO2_CONFIG: CustomBiomarkerConfig = {
    name: 'CO2',
    zoneCount: 3,
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 22,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.LOW,
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 22,
            max: 32,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Optimal',
        },
        {
            label: 'High',
            min: 32,
            max: 40,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

// ============================================================================
// Phase 2.3: Gender-Specific Uric Acid
// ============================================================================

const URIC_ACID_MEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Uric Acid',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 3.7,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 3.7,
            max: 8.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 8.0,
            max: 10.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

const URIC_ACID_WOMEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Uric Acid',
    zoneCount: 3,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Below Range',
            min: 0,
            max: 2.6,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red for Red-Green-Red pattern
            status: 'Low',
        },
        {
            label: 'In Range',
            min: 2.6,
            max: 6.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL,
            status: 'Normal',
        },
        {
            label: 'Above Range',
            min: 6.0,
            max: 7.5,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH,
            status: 'High',
        },
    ],
};

// ============================================================================
// Phase 2.4: Vitamin D Special Case (Deficient-Optimal-Toxic)
// ============================================================================

const VITAMIN_D_CONFIG: CustomBiomarkerConfig = {
    name: 'Vitamin D',
    zoneCount: 3,
    zones: [
        {
            label: 'Deficient',
            min: 0,
            max: 30,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.DEFICIENT, // Orange
            status: 'Deficient',
        },
        {
            label: 'Optimal',
            min: 30,
            max: 100,
            percentage: 33.33,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
        {
            label: 'Toxic',
            min: 100,
            max: 150,
            percentage: 33.34,
            color: BIOMARKER_STATUS_COLORS.TOXIC, // Red
            status: 'Toxic',
        },
    ],
};

// ============================================================================
// Phase 2.5: Green-Red (80/20) 2-Zone Disease Markers (Lower is Better)
// ============================================================================

const CA_125_CONFIG: CustomBiomarkerConfig = {
    name: 'CA-125',
    zoneCount: 2,
    zones: [
        {
            label: 'Negative',
            min: 0,
            max: 35,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NEGATIVE, // Green
            status: 'Negative',
        },
        {
            label: 'Positive',
            min: 35,
            max: 100,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.POSITIVE, // Pink/Red
            status: 'Positive',
        },
    ],
};

const RF_CONFIG: CustomBiomarkerConfig = {
    name: 'RF',
    zoneCount: 2,
    zones: [
        {
            label: 'Negative',
            min: 0,
            max: 14,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NEGATIVE, // Green
            status: 'Negative',
        },
        {
            label: 'Positive',
            min: 14,
            max: 50,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.POSITIVE, // Pink/Red
            status: 'Positive',
        },
    ],
};

const ANA_CONFIG: CustomBiomarkerConfig = {
    name: 'ANA',
    zoneCount: 2,
    zones: [
        {
            label: 'Negative',
            min: 0,
            max: 1.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NEGATIVE, // Green
            status: 'Negative',
        },
        {
            label: 'Positive',
            min: 1.0,
            max: 10.0,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.POSITIVE, // Pink/Red
            status: 'Positive',
        },
    ],
};

const ANTI_TPO_CONFIG: CustomBiomarkerConfig = {
    name: 'Anti-TPO',
    zoneCount: 2,
    zones: [
        {
            label: 'Negative',
            min: 0,
            max: 34,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NEGATIVE, // Green
            status: 'Negative',
        },
        {
            label: 'Positive',
            min: 34,
            max: 100,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.POSITIVE, // Pink/Red
            status: 'Positive',
        },
    ],
};

const CEA_CONFIG: CustomBiomarkerConfig = {
    name: 'CEA',
    zoneCount: 2,
    zones: [
        {
            label: 'Negative',
            min: 0,
            max: 3.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NEGATIVE, // Green
            status: 'Negative',
        },
        {
            label: 'Positive',
            min: 3.0,
            max: 10.0,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.POSITIVE, // Pink/Red
            status: 'Positive',
        },
    ],
};

const CRP_CONFIG: CustomBiomarkerConfig = {
    name: 'CRP',
    zoneCount: 2,
    zones: [
        {
            label: 'Negative',
            min: 0,
            max: 3.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NEGATIVE, // Green
            status: 'Negative',
        },
        {
            label: 'Positive',
            min: 3.0,
            max: 10.0,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.POSITIVE, // Pink/Red
            status: 'Positive',
        },
    ],
};

// ============================================================================
// Phase 2.6: Green-Orange-Red (70/20/10) 3-Zone Biomarkers
// ============================================================================

const GLUCOSE_CONFIG: CustomBiomarkerConfig = {
    name: 'Glucose',
    zoneCount: 3,
    forceCustomRange: true, // Force custom 3-zone layout (Optimal-Borderline-High) for diabetes screening
    zones: [
        {
            label: 'Optimal',
            min: 0,
            max: 99,
            percentage: 70,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 99,
            max: 126,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.BORDERLINE, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 126,
            max: 200,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red
            status: 'High',
        },
    ],
};

const HBA1C_CONFIG: CustomBiomarkerConfig = {
    name: 'HbA1c',
    zoneCount: 3,
    forceCustomRange: true, // Force custom 3-zone layout (Optimal-Borderline-High) for diabetes screening
    zones: [
        {
            label: 'Optimal',
            min: 0,
            max: 5.7,
            percentage: 70,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 5.7,
            max: 6.5,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.BORDERLINE, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 6.5,
            max: 10.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red
            status: 'High',
        },
    ],
};

const HOMOCYSTEINE_CONFIG: CustomBiomarkerConfig = {
    name: 'Homocysteine',
    zoneCount: 3,
    forceCustomRange: true, // Force custom 3-zone layout (Optimal-Borderline-High)
    zones: [
        {
            label: 'Optimal',
            min: 0,
            max: 10.4,
            percentage: 70,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 10.4,
            max: 15.0,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.BORDERLINE, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 15.0,
            max: 30.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red
            status: 'High',
        },
    ],
};

const FREE_PSA_CONFIG: CustomBiomarkerConfig = {
    name: 'Free PSA',
    zoneCount: 3,
    forceCustomRange: true, // Force custom 3-zone layout (Optimal-Borderline-High)
    zones: [
        {
            label: 'Optimal',
            min: 0,
            max: 0.5,
            percentage: 70,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
        {
            label: 'Borderline',
            min: 0.5,
            max: 2.5,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.BORDERLINE, // Orange
            status: 'Borderline',
        },
        {
            label: 'High',
            min: 2.5,
            max: 4.0,
            percentage: 10,
            color: BIOMARKER_STATUS_COLORS.HIGH, // Red
            status: 'High',
        },
    ],
};

// ============================================================================
// Phase 2.7: Red-Green (20/80) 2-Zone Biomarkers (Higher is Better)
// ============================================================================

const FERRITIN_MEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Ferritin',
    zoneCount: 2,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 24,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 24,
            max: 336,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
    ],
};

const FERRITIN_WOMEN_CONFIG: CustomBiomarkerConfig = {
    name: 'Ferritin',
    zoneCount: 2,
    genderSpecific: true,
    forceCustomRange: true,
    zones: [
        {
            label: 'Low',
            min: 0,
            max: 11,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.LOW, // Orange
            status: 'Low',
        },
        {
            label: 'Optimal',
            min: 11,
            max: 307,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
    ],
};

const EGFR_CONFIG: CustomBiomarkerConfig = {
    name: 'eGFR',
    zoneCount: 2,
    zones: [
        {
            label: 'Impaired',
            min: 0,
            max: 60,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.IMPAIRED, // Orange
            status: 'Impaired',
        },
        {
            label: 'Normal',
            min: 60,
            max: 120,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Normal',
        },
    ],
};

const VITAMIN_B12_CONFIG: CustomBiomarkerConfig = {
    name: 'Vitamin B12',
    zoneCount: 2,
    zones: [
        {
            label: 'Deficient',
            min: 0,
            max: 200,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.DEFICIENT, // Orange
            status: 'Deficient',
        },
        {
            label: 'Optimal',
            min: 200,
            max: 1000,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
    ],
};

const FOLATE_CONFIG: CustomBiomarkerConfig = {
    name: 'Folate',
    zoneCount: 2,
    zones: [
        {
            label: 'Deficient',
            min: 0,
            max: 3.0,
            percentage: 20,
            color: BIOMARKER_STATUS_COLORS.DEFICIENT, // Orange
            status: 'Deficient',
        },
        {
            label: 'Optimal',
            min: 3.0,
            max: 20.0,
            percentage: 80,
            color: BIOMARKER_STATUS_COLORS.NORMAL, // Green
            status: 'Optimal',
        },
    ],
};

/**
 * Map of custom biomarker configurations by normalized test name
 */
const CUSTOM_BIOMARKER_MAP: Record<string, CustomBiomarkerConfig> = {
    cortisol: CORTISOL_CONFIG,
    "dhea's": DHEAS_CONFIG,
    dheas: DHEAS_CONFIG,
    // Hormone biomarkers with value-only display
    estradiol: ESTRADIOL_CONFIG,
    estrogen: ESTRADIOL_CONFIG,
    fsh: FSH_CONFIG,
    'follicle stimulating hormone': FSH_CONFIG,
    'follicle-stimulating hormone': FSH_CONFIG,
    lh: LH_CONFIG,
    'luteinizing hormone': LH_CONFIG,
    'luteinising hormone': LH_CONFIG,
    progesterone: PROGESTERONE_CONFIG,
    prog: PROGESTERONE_CONFIG,
    // Thyroid biomarkers
    'free t3': FREE_T3_CONFIG,
    ft3: FREE_T3_CONFIG,
    'triiodothyronine free': FREE_T3_CONFIG,
    'free t4': FREE_T4_CONFIG,
    ft4: FREE_T4_CONFIG,
    'thyroxine free': FREE_T4_CONFIG,
    't3 total': T3_TOTAL_CONFIG,
    t3: T3_TOTAL_CONFIG,
    'total t3': T3_TOTAL_CONFIG,
    triiodothyronine: T3_TOTAL_CONFIG,
    't4 total': T4_TOTAL_CONFIG,
    t4: T4_TOTAL_CONFIG,
    'total t4': T4_TOTAL_CONFIG,
    thyroxine: T4_TOTAL_CONFIG,
    tsh: TSH_CONFIG,
    'thyroid stimulating hormone': TSH_CONFIG,
    'thyroid-stimulating hormone': TSH_CONFIG,
    thyrotropin: TSH_CONFIG,
    'thyroglobulin antibody': THYROGLOBULIN_ANTIBODY_CONFIG,
    'tg antibody': THYROGLOBULIN_ANTIBODY_CONFIG,
    'anti-thyroglobulin': THYROGLOBULIN_ANTIBODY_CONFIG,
    'thyroid peroxidase antibody': THYROID_PEROXIDASE_ANTIBODY_CONFIG,
    'tpo antibody': THYROID_PEROXIDASE_ANTIBODY_CONFIG,
    tpoab: THYROID_PEROXIDASE_ANTIBODY_CONFIG,
    'total cholesterol': TOTAL_CHOLESTEROL_CONFIG,
    cholesterol: TOTAL_CHOLESTEROL_CONFIG, // Alias for "Cholesterol" test name
    'hdl direct': HDL_DIRECT_MEN_CONFIG,
    'direct hdl': HDL_DIRECT_MEN_CONFIG, // Alias for "Direct HDL" test name
    'ldl direct': LDL_DIRECT_CONFIG,
    'direct ldl': LDL_DIRECT_CONFIG, // Alias for "Direct LDL" test name
    ldl: LDL_DIRECT_CONFIG, // Alias - LDL is same as LDL Direct
    'vldl-c': VLDL_C_CONFIG,
    vldl: VLDL_C_CONFIG,
    triglycerides: TRIGLYCERIDES_CONFIG,
    hscrp: HSCRP_CONFIG,
    'hs-crp': HSCRP_CONFIG,
    'ck-mb': CK_MB_CONFIG,
    ckmb: CK_MB_CONFIG,
    'lipoprotein (a)': LIPOPROTEIN_A_CONFIG,
    'lipoprotein a': LIPOPROTEIN_A_CONFIG,
    'lp(a)': LIPOPROTEIN_A_CONFIG,
    'apolipoprotein b': APOLIPOPROTEIN_B_CONFIG,
    'apo b': APOLIPOPROTEIN_B_CONFIG,
    'cholesterol to hdl ratio': CHOLESTEROL_HDL_RATIO_CONFIG,
    'cholesterol/hdl ratio': CHOLESTEROL_HDL_RATIO_CONFIG, // Alias for "Cholesterol/HDL Ratio"
    'chol/hdl ratio': CHOLESTEROL_HDL_RATIO_CONFIG,
    // Phase 2.1: Standard Red-Green-Red (10/80/10) Biomarkers
    albumin: ALBUMIN_CONFIG,
    'serum albumin': ALBUMIN_CONFIG,
    alt: ALT_CONFIG,
    'alanine aminotransferase': ALT_CONFIG,
    sgpt: ALT_CONFIG,
    amylase: AMYLASE_CONFIG,
    ast: AST_CONFIG,
    'aspartate aminotransferase': AST_CONFIG,
    sgot: AST_CONFIG,
    bilirubin: BILIRUBIN_CONFIG,
    bun: BUN_CONFIG,
    'blood urea nitrogen': BUN_CONFIG,
    'urea nitrogen': BUN_CONFIG,
    chloride: CHLORIDE_CONFIG,
    ck: CK_CONFIG,
    'creatine kinase': CK_CONFIG,
    cpk: CK_CONFIG,
    creatinine: CREATININE_CONFIG,
    'serum creatinine': CREATININE_CONFIG,
    'direct bilirubin': DIRECT_BILIRUBIN_CONFIG,
    'bilirubin direct': DIRECT_BILIRUBIN_CONFIG,
    dtibc: DTIBC_CONFIG,
    'enzymatic co2': ENZYMATIC_CO2_CONFIG,
    'carbon dioxide': ENZYMATIC_CO2_CONFIG,
    ggt: GGT_CONFIG,
    'gamma gt': GGT_CONFIG,
    'gamma-glutamyl transferase': GGT_CONFIG,
    insulin: INSULIN_CONFIG,
    'fasting insulin': INSULIN_CONFIG,
    iron: IRON_CONFIG,
    'serum iron': IRON_CONFIG,
    magnesium: MAGNESIUM_CONFIG,
    mg: MAGNESIUM_CONFIG,
    phosphorus: PHOSPHORUS_CONFIG,
    phosphate: PHOSPHORUS_CONFIG,
    potassium: POTASSIUM_CONFIG,
    k: POTASSIUM_CONFIG,
    sodium: SODIUM_CONFIG,
    na: SODIUM_CONFIG,
    tibc: TIBC_CONFIG,
    'total iron binding capacity': TIBC_CONFIG,
    'total bilirubin': TOTAL_BILIRUBIN_CONFIG,
    'bilirubin total': TOTAL_BILIRUBIN_CONFIG,
    'total protein': TOTAL_PROTEIN_CONFIG,
    'serum protein': TOTAL_PROTEIN_CONFIG,
    uibc: UIBC_CONFIG,
    'unsaturated iron binding capacity': UIBC_CONFIG,
    // Phase 2.2: Red-Green-Red with custom labels
    'bun/creat ratio': BUN_CREAT_RATIO_CONFIG,
    'bun/creatinine ratio': BUN_CREAT_RATIO_CONFIG,
    calcium: CALCIUM_CONFIG,
    'serum calcium': CALCIUM_CONFIG,
    ca: CALCIUM_CONFIG,
    co2: CO2_CONFIG,
    'carbon dioxide total': CO2_CONFIG,
    bicarbonate: CO2_CONFIG,
    // Phase 2.4: Vitamin D special case
    'vitamin d': VITAMIN_D_CONFIG,
    '25-oh vitamin d': VITAMIN_D_CONFIG,
    '25-hydroxyvitamin d': VITAMIN_D_CONFIG,
    'vitamin d 25-hydroxy': VITAMIN_D_CONFIG,
    // Phase 2.5: Green-Red 2-zone disease markers
    'ca-125': CA_125_CONFIG,
    'ca 125': CA_125_CONFIG,
    'cancer antigen 125': CA_125_CONFIG,
    rf: RF_CONFIG,
    'rheumatoid factor': RF_CONFIG,
    ana: ANA_CONFIG,
    'antinuclear antibody': ANA_CONFIG,
    'anti-nuclear antibody': ANA_CONFIG,
    'anti-tpo': ANTI_TPO_CONFIG,
    'anti tpo': ANTI_TPO_CONFIG,
    'antithyroid peroxidase': ANTI_TPO_CONFIG,
    cea: CEA_CONFIG,
    'carcinoembryonic antigen': CEA_CONFIG,
    crp: CRP_CONFIG,
    'c-reactive protein': CRP_CONFIG,
    'c reactive protein': CRP_CONFIG,
    // Phase 2.6: Green-Orange-Red (70/20/10) biomarkers
    glucose: GLUCOSE_CONFIG,
    'blood glucose': GLUCOSE_CONFIG,
    'fasting glucose': GLUCOSE_CONFIG,
    'serum glucose': GLUCOSE_CONFIG,
    hba1c: HBA1C_CONFIG,
    'hb a1c': HBA1C_CONFIG,
    'hemoglobin a1c': HBA1C_CONFIG,
    'glycated hemoglobin': HBA1C_CONFIG,
    a1c: HBA1C_CONFIG,
    homocysteine: HOMOCYSTEINE_CONFIG,
    'plasma homocysteine': HOMOCYSTEINE_CONFIG,
    'free psa': FREE_PSA_CONFIG,
    'free prostate specific antigen': FREE_PSA_CONFIG,
    fpsa: FREE_PSA_CONFIG,
    // Phase 2.7: Red-Green 2-zone biomarkers (higher is better)
    egfr: EGFR_CONFIG,
    'estimated gfr': EGFR_CONFIG,
    'glomerular filtration rate': EGFR_CONFIG,
    gfr: EGFR_CONFIG,
    'vitamin b12': VITAMIN_B12_CONFIG,
    'vitamin b-12': VITAMIN_B12_CONFIG,
    b12: VITAMIN_B12_CONFIG,
    'b-12': VITAMIN_B12_CONFIG,
    cobalamin: VITAMIN_B12_CONFIG,
    folate: FOLATE_CONFIG,
    'folic acid': FOLATE_CONFIG,
    'serum folate': FOLATE_CONFIG,
};

/**
 * Normalize test name for lookup
 */
export const normalizeTestName = (testName: string): string => {
    return testName.toLowerCase().trim();
};

/**
 * Get custom biomarker configuration if it exists
 * @param testName - The name of the biomarker/test
 * @param gender - Optional gender for gender-specific configs (e.g., HDL Direct, Uric Acid, Ferritin)
 * @returns CustomBiomarkerConfig or null if no custom config exists
 */
export const getCustomBiomarkerConfig = (
    testName: string,
    gender?: 'male' | 'female'
): CustomBiomarkerConfig | null => {
    const normalized = normalizeTestName(testName);

    // Handle gender-specific configs
    if (normalized === 'hdl direct' && gender) {
        return gender === 'female' ? HDL_DIRECT_WOMEN_CONFIG : HDL_DIRECT_MEN_CONFIG;
    }

    if (normalized === 'uric acid' && gender) {
        return gender === 'female' ? URIC_ACID_WOMEN_CONFIG : URIC_ACID_MEN_CONFIG;
    }

    if (normalized === 'ferritin' && gender) {
        return gender === 'female' ? FERRITIN_WOMEN_CONFIG : FERRITIN_MEN_CONFIG;
    }

    // Hormone biomarkers - gender-specific
    if (normalized === 'testosterone' && gender) {
        return gender === 'female' ? TESTOSTERONE_WOMEN_CONFIG : TESTOSTERONE_MEN_CONFIG;
    }

    if (normalized === 'bioavailable testosterone' && gender) {
        return gender === 'female' ? BIOAVAILABLE_TESTOSTERONE_WOMEN_CONFIG : BIOAVAILABLE_TESTOSTERONE_MEN_CONFIG;
    }

    if (normalized === 'free testosterone' && gender) {
        return gender === 'female' ? FREE_TESTOSTERONE_WOMEN_CONFIG : FREE_TESTOSTERONE_MEN_CONFIG;
    }

    if (normalized === 'shbg' && gender) {
        return gender === 'female' ? SHBG_WOMEN_CONFIG : SHBG_MEN_CONFIG;
    }

    if (normalized === 'prolactin' && gender) {
        return gender === 'female' ? PROLACTIN_WOMEN_CONFIG : PROLACTIN_MEN_CONFIG;
    }

    return CUSTOM_BIOMARKER_MAP[normalized] || null;
};

/**
 * Check if a biomarker should display value only (no chart/progress bar)
 */
export const isValueOnlyDisplay = (testName: string, displayContext?: 'dashboard' | 'singleResult'): boolean => {
    const config = getCustomBiomarkerConfig(testName);
    if (!config || !config.valueOnlyDisplay) return false;

    // Check if the display context matches
    if (config.displayContext) {
        return config.displayContext === displayContext || config.displayContext === 'both';
    }

    return true;
};

/**
 * Get the zone count for a biomarker (2, 3, or 4)
 */
export const getZoneCount = (testName: string): 2 | 3 | 4 => {
    const config = getCustomBiomarkerConfig(testName);
    return config?.zoneCount || 3;
};

/**
 * Check if a biomarker has a custom configuration
 */
export const hasCustomConfig = (testName: string): boolean => {
    const normalized = normalizeTestName(testName);
    return normalized in CUSTOM_BIOMARKER_MAP;
};
